import { strapiClient } from "@shared/api";
import rebuild from "@shared/api/rebuild";
import { NotFoundError } from "@shared/utils/not-found-error";
import { getSeoConfig } from "@shared/utils/seo/seo-helper";

import { getMapPoints } from "@/_pages/map/api";
import type { MapPageType } from "@/_pages/map/model";

import { mapDtoToCafesRestaurantsPoints } from "../model";
import type { CafesRestaurantsPointType } from "../model/types";

type CafesRestaurantsPointPageResponse = {
  point: CafesRestaurantsPointType;
  mapData: Pick<
    MapPageType,
    "webcams" | "cafes-restaurants" | "accommodations" | "cultural-objects"
  >;
  seo: ReturnType<(typeof getSeoConfig)["cafesRestaurantsPoint"]>;
};

export const getCafesRestaurantsPointPage = async (
  slug: string,
): Promise<CafesRestaurantsPointPageResponse> => {
  const searchParams = {
    filters: {
      uid: {
        $eq: slug,
      },
    },
    populate: {
      photos: {
        populate: "*",
      },
      workTimeList: {
        populate: "*",
      },
      seo: {
        populate: {
          ogImage: {
            populate: "*",
          },
        },
      },
      content: {
        on: {
          "article.text-block": {
            populate: "*",
          },
          "article.quote-with-author": {
            populate: "*",
          },
          "article.table": {
            populate: {
              tabletRows: {
                populate: "*",
              },
              columnTitle: {
                populate: "*",
              },
              columnSubtitle: {
                populate: "*",
              },
            },
          },
          "article.gallery": {
            populate: "*",
          },
          "article.video": {
            populate: "*",
          },
          "article.list-with-icon": {
            populate: {
              icon: {
                populate: "*",
              },
              items: {
                populate: "*",
              },
            },
          },
          "article.conditions-table": {
            populate: {
              rows: {
                populate: "*",
              },
            },
          },
          "article.accordions-text": {
            populate: "*",
          },
        },
      },
      other_cafes_restaurants: {
        populate: {
          photos: {
            populate: "*",
          },
          workTimeList: {
            populate: "*",
          },
          cafes_restaurants_types: {
            populate: "*",
          },
          cafes_restaurants_badge_types: {
            populate: "*",
          },
        },
      },
      cafes_restaurants_types: {
        populate: "*",
      },
      cafes_restaurants_badge_types: {
        populate: "*",
      },
    },
  };

  const cafesRestaurantsPoints = strapiClient.collection(
    "cafes-restaurants-points",
  );

  const page = await cafesRestaurantsPoints.find({ ...searchParams });

  const points = mapDtoToCafesRestaurantsPoints(page).data;
  const point = points[0];

  if (!point) {
    throw new NotFoundError(
      `Cafes/restaurants point with slug "${slug}" not found`,
    );
  }

  const mapData = await getMapPoints();
  const seo = getSeoConfig.cafesRestaurantsPoint(point);

  return rebuild({
    point: {
      ...point,
      seo,
    } as CafesRestaurantsPointType,
    mapData,
    seo,
  }) as CafesRestaurantsPointPageResponse;
};
